<template>
    <NuxtLayout>
        <NuxtPage :page-key="route => route.fullPath" />
    </NuxtLayout>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useGeneralStore } from '@core/stores/general';

const generalStore = useGeneralStore();
const webConfig = computed(() => generalStore.webConfig);

// Tenant <head> tags (the Go catch-all used to fill these into index.html)
const tenantName = computed(() => webConfig.value.tenantName || '');
const fullName = computed(() => `${tenantName.value} - ${webConfig.value.appName || ''}`);

// Absolute URL of the current page, without its query: the tenant's base URL (the site's root),
// or the requested origin when it isn't set, plus the app's base path and the route's path.
const route = useRoute();
const requestOrigin = useRequestURL({ xForwardedHost: true }).origin;
const basePath = useRuntimeConfig().app.baseURL.replace(/\/+$/, '');
const pageUrl = computed(() => {
    const origin = (webConfig.value.baseUrl || requestOrigin).replace(/\/+$/, '');

    return `${origin}${basePath}${route!.path}`;
});

useHead({
    // Pages may set their own title (e.g. the event name); otherwise "<tenant> | <app>".
    titleTemplate: (title?: string) => title
        ? `${title} | ${tenantName.value}`
        : `${tenantName.value} | ${webConfig.value.appName || ''}`, 
    link: [
        { rel: 'canonical', href: pageUrl },
        { rel: 'alternate', hreflang: 'x-default', href: pageUrl },
        { rel: 'shortcut icon', type: 'image/x-icon', href: () => webConfig.value.faviconUrl },
        { rel: 'apple-touch-icon', href: () => webConfig.value.faviconUrl },
        { rel: 'icon', type: 'image/png', href: () => webConfig.value.faviconUrl },
    ],
});

useSeoMeta({
    description: () => webConfig.value.description || tenantName,
    ogTitle: fullName,
    ogDescription: tenantName,
    ogUrl: pageUrl,
    ogSiteName: tenantName,
    ogImage: () => webConfig.value.logoUrl,
    ogImageAlt: fullName,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: fullName,
    twitterDescription: tenantName,
    twitterImage: () => webConfig.value.logoUrl,
});
</script>
